import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Container from './shared/Container';

const stats = [
  { endVal: 60, suffix: '%', label: 'Faster Processing', duration: 1200 },
  { endVal: 40, suffix: '%', label: 'Error Reduction', duration: 1200 },
  { endVal: 15, suffix: '%', label: 'Cost Savings', duration: 1000 },
  { endVal: 8, suffix: '×', finalOverride: '4–8×', label: 'Return on Investment', duration: 1400 },
];

// ─── Slide data — each shows a different AI output type ──────────────────────

const DOCS = [
  {
    filename: 'application_4521.pdf',
    tag: 'Application',
    tagColor: '#60a5fa',
    lines: [82, 55, 90, 40, 72, 60, 85, 45, 68, 50],
    outputType: 'fields',
    outputLabel: 'Extracted',
    fields: [
      { label: 'Applicant', value: 'J. Williams' },
      { label: 'Request Type', value: 'Benefits Enrollment' },
      { label: 'Filed', value: 'Feb 14, 2026' },
      { label: 'Amount', value: '$4,200 / mo' },
      { label: 'Risk Score', value: 'Low Risk', accent: '#4ade80' },
    ],
  },
  {
    filename: 'eligibility_policy.pdf',
    tag: 'Policy Extraction',
    tagColor: '#a78bfa',
    lines: [88, 60, 76, 50, 92, 44, 68, 80, 56, 64],
    outputType: 'logic',
    outputLabel: 'Decision Logic',
    rules: [
      {
        type: 'all', color: '#4ade80',
        children: ['Employment: Full-time', 'Hours ≥ 30 / week'],
      },
      {
        type: 'any', color: '#60a5fa',
        children: ['Status: Permanent', 'Is Active Member'],
      },
      {
        type: 'none', color: '#f87171',
        children: ['Code: Temporary', 'Seasonal Employee'],
      },
    ],
  },
  {
    filename: 'review_request_2847.pdf',
    tag: 'Review',
    tagColor: '#818cf8',
    lines: [85, 62, 78, 48, 90, 55, 72, 65, 80, 52],
    outputType: 'fields',
    outputLabel: 'Extracted',
    fields: [
      { label: 'Applicant', value: 'M. Chen' },
      { label: 'Category', value: 'Group Benefits' },
      { label: 'Role', value: 'Software Engineer' },
      { label: 'Effective Period', value: 'To age 65' },
      { label: 'Decision', value: 'Approved', accent: '#4ade80' },
    ],
  },
  {
    filename: 'supporting_doc_0092.pdf',
    tag: 'Document AI',
    tagColor: '#34d399',
    lines: [75, 88, 50, 65, 80, 45, 92, 58, 72, 60],
    outputType: 'timeline',
    outputLabel: 'Event Timeline',
    events: [
      { date: 'Jan 8', text: 'Initial assessment', color: '#60a5fa' },
      { date: 'Jan 28', text: 'Evaluation completed', color: '#60a5fa' },
      { date: 'Feb 3', text: 'Processing ongoing', color: '#4ade80' },
      { date: 'Feb 14', text: 'Request filed', color: '#D8572A' },
      { date: 'Feb 20', text: 'Review in progress', color: '#f59e0b' },
    ],
  },
];

const TICKER = [
  { text: 'Application #4521 approved — 98% confidence', color: '#4ade80' },
  { text: 'Eligibility policy parsed — 6 rules extracted', color: '#a78bfa' },
  { text: 'Review decision: approved — 3 rules matched', color: '#818cf8' },
  { text: 'Document #0092 — 5 events extracted', color: '#34d399' },
  { text: 'Request #892 processed in 2.1s', color: '#4ade80' },
  { text: 'Annotation complete — 14 fields tagged', color: '#60a5fa' },
  { text: 'Eligibility: Group Benefits approved', color: '#60a5fa' },
  { text: 'Decision flow compiled — 3 rule groups', color: '#a78bfa' },
];

// ─── Output panel renderers ───────────────────────────────────────────────────

const FieldsOutput = ({ fields, animKey }) => (
  <>
    {fields.map(({ label, value, accent }, i) => (
      <div
        key={`${animKey}-${label}`}
        className="mb-3"
        style={{ animation: `heroFadeIn 0.4s ease-out ${i * 0.3 + 0.25}s both` }}
      >
        <div className="leading-none mb-0.5" style={{ fontSize: '0.52rem', color: 'rgba(255,255,255,0.3)' }}>{label}</div>
        <div
          className="font-semibold leading-snug flex items-center gap-1"
          style={{ fontSize: '0.63rem', color: accent || 'rgba(255,255,255,0.72)' }}
        >
          {accent && <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: accent }} />}
          {value}
        </div>
      </div>
    ))}
  </>
);

const LogicOutput = ({ rules, animKey }) => (
  <>
    {rules.map((group, gi) => (
      <div
        key={`${animKey}-rule-${gi}`}
        className="mb-2.5"
        style={{ animation: `heroFadeIn 0.4s ease-out ${gi * 0.45 + 0.2}s both` }}
      >
        {/* Group label */}
        <div className="flex items-center gap-1 mb-1">
          <span
            className="font-bold tracking-widest uppercase"
            style={{ fontSize: '0.44rem', color: group.color }}
          >
            {group.type === 'all' ? '✓ ALL' : group.type === 'any' ? '◑ ANY' : '✗ NONE'}
          </span>
        </div>
        {/* Children */}
        <div style={{ paddingLeft: '8px', borderLeft: `1.5px solid ${group.color}44` }}>
          {group.children.map((child, ci) => (
            <div
              key={ci}
              className="flex items-start gap-1 mb-1"
              style={{ animation: `heroFadeIn 0.3s ease-out ${gi * 0.45 + ci * 0.18 + 0.4}s both` }}
            >
              <span
                className="inline-block rounded-full flex-shrink-0 mt-[4px]"
                style={{ width: '4px', height: '4px', background: group.color, opacity: 0.7 }}
              />
              <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.6)', lineHeight: 1.4 }}>{child}</span>
            </div>
          ))}
        </div>
      </div>
    ))}
  </>
);

const FlagsOutput = ({ flags, animKey }) => (
  <>
    {flags.map(({ label, detail, color }, i) => (
      <div
        key={`${animKey}-flag-${i}`}
        className="mb-3"
        style={{ animation: `heroFadeIn 0.4s ease-out ${i * 0.3 + 0.25}s both` }}
      >
        <div className="flex items-center gap-1 mb-0.5">
          <span style={{ fontSize: '0.48rem', color }}>▲</span>
          <span className="font-semibold" style={{ fontSize: '0.6rem', color }}>{label}</span>
        </div>
        <div style={{ fontSize: '0.53rem', color: 'rgba(255,255,255,0.35)', paddingLeft: '10px' }}>{detail}</div>
      </div>
    ))}
  </>
);

const TimelineOutput = ({ events, animKey }) => (
  <>
    {events.map(({ date, text, color }, i) => (
      <div
        key={`${animKey}-evt-${i}`}
        className="flex gap-2 mb-2.5"
        style={{ animation: `heroFadeIn 0.4s ease-out ${i * 0.28 + 0.2}s both` }}
      >
        <div className="flex flex-col items-center flex-shrink-0">
          <div
            className="rounded-full"
            style={{ width: '6px', height: '6px', background: color, marginTop: '3px', flexShrink: 0 }}
          />
          {i < events.length - 1 && (
            <div style={{ width: '1px', flex: 1, background: 'rgba(255,255,255,0.08)', marginTop: '2px', minHeight: '10px' }} />
          )}
        </div>
        <div style={{ paddingBottom: i < events.length - 1 ? '4px' : '0' }}>
          <div style={{ fontSize: '0.48rem', color: 'rgba(255,255,255,0.3)', marginBottom: '1px' }}>{date}</div>
          <div className="font-medium" style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.7)' }}>{text}</div>
        </div>
      </div>
    ))}
  </>
);

// ─── Hero Visual ──────────────────────────────────────────────────────────────

const HeroVisual = () => {
  const [docIdx, setDocIdx] = useState(0);
  const [animKey, setAnimKey] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setDocIdx(i => (i + 1) % DOCS.length);
      setAnimKey(k => k + 1);
    }, 5000);
    return () => clearInterval(id);
  }, []);

  const doc = DOCS[docIdx];

  return (
    <div
      className="relative w-full max-w-md mx-auto lg:mx-0 lg:ml-auto select-none rounded-2xl overflow-hidden"
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.09)',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 0 80px rgba(216,87,42,0.10), 0 0 0 1px rgba(255,255,255,0.05)',
      }}
    >
      {/* Title bar */}
      <div
        className="flex items-center gap-2.5 px-4 py-3 border-b"
        style={{ borderColor: 'rgba(255,255,255,0.07)' }}
      >
        <div className="flex gap-1.5">
          {[0, 1, 2].map(i => (
            <div key={i} className="w-2 h-2 rounded-full" style={{ background: 'rgba(255,255,255,0.09)' }} />
          ))}
        </div>
        <div className="flex items-center gap-1.5 ml-2 min-w-0 flex-1 overflow-hidden">
          <svg className="h-3 w-3 text-zinc-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span
            key={`fn-${animKey}`}
            className="text-zinc-500 truncate"
            style={{ fontSize: '0.6rem', animation: 'heroFadeSlide 0.35s ease-out both' }}
          >
            {doc.filename}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span
            key={`tag-${animKey}`}
            className="font-bold tracking-widest uppercase px-1.5 py-0.5 rounded"
            style={{
              fontSize: '0.48rem',
              color: doc.tagColor,
              background: `${doc.tagColor}18`,
              animation: 'heroFadeSlide 0.35s ease-out both',
            }}
          >
            {doc.tag}
          </span>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-vermillion-500" style={{ animation: 'heroBlink 2s ease-in-out infinite' }} />
            <span className="font-semibold text-zinc-600" style={{ fontSize: '0.48rem' }}>4 active</span>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex" style={{ height: '236px' }}>

        {/* Left: document text */}
        <div className="flex-1 p-5 relative overflow-hidden">
          {doc.lines.map((w, i) => {
            const isFlagged = doc.flagLines?.includes(i);
            return (
              <div
                key={`${animKey}-ln-${i}`}
                className="rounded-full"
                style={{
                  height: '7px',
                  width: `${w}%`,
                  marginBottom: '13px',
                  background: isFlagged
                    ? 'rgba(248,113,113,0.4)'
                    : 'rgba(255,255,255,0.07)',
                  boxShadow: isFlagged ? '0 0 6px rgba(248,113,113,0.3)' : 'none',
                  animation: `heroLineIn 0.3s ease-out ${i * 0.028}s both`,
                }}
              />
            );
          })}
          {/* Scanning highlight */}
          <div
            className="absolute left-0 right-0 pointer-events-none"
            style={{
              height: '32px',
              background: 'linear-gradient(to bottom, transparent, rgba(216,87,42,0.13), transparent)',
              animation: 'heroScan 3.5s linear 0.6s infinite',
              top: '-32px',
            }}
          />
        </div>

        {/* Divider */}
        <div style={{ width: '1px', background: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />

        {/* Right: output panel — content switches per slide */}
        <div className="flex flex-col overflow-hidden" style={{ width: '158px' }}>
          {/* Output label */}
          <div
            key={`lbl-${animKey}`}
            className="px-3.5 pt-3.5 pb-2 font-bold tracking-[0.14em] uppercase flex-shrink-0"
            style={{ fontSize: '0.48rem', color: doc.tagColor, animation: 'heroFadeSlide 0.35s ease-out both' }}
          >
            {doc.outputLabel}
          </div>

          <div className="px-3.5 pb-3 flex-1 overflow-hidden">
            {doc.outputType === 'fields' && (
              <FieldsOutput fields={doc.fields} animKey={animKey} />
            )}
            {doc.outputType === 'logic' && (
              <LogicOutput rules={doc.rules} animKey={animKey} />
            )}
            {doc.outputType === 'flags' && (
              <FlagsOutput flags={doc.flags} animKey={animKey} />
            )}
            {doc.outputType === 'timeline' && (
              <TimelineOutput events={doc.events} animKey={animKey} />
            )}
          </div>
        </div>
      </div>

      {/* Divider */}
      <div style={{ height: '1px', background: 'rgba(255,255,255,0.07)' }} />

      {/* Live activity ticker */}
      <div
        className="py-2 overflow-hidden"
        style={{ mask: 'linear-gradient(to right, transparent 0%, black 8%, black 92%, transparent 100%)' }}
      >
        <div
          style={{
            display: 'flex',
            gap: '28px',
            animation: 'heroMarquee 30s linear infinite',
            whiteSpace: 'nowrap',
          }}
        >
          {[...TICKER, ...TICKER].map((e, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 flex-shrink-0 px-2" style={{ fontSize: '0.53rem' }}>
              <span className="w-1.5 h-1.5 rounded-full inline-block flex-shrink-0" style={{ background: e.color }} />
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>{e.text}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Progress dots */}
      <div className="flex items-center justify-between px-4 pb-3" style={{ marginTop: '-2px' }}>
        <div className="flex gap-1.5">
          {DOCS.map((_, i) => (
            <div
              key={i}
              className="rounded-full transition-all duration-500"
              style={{
                height: '3px',
                width: i === docIdx ? '16px' : '6px',
                background: i === docIdx ? DOCS[i].tagColor : 'rgba(255,255,255,0.12)',
              }}
            />
          ))}
        </div>
        <span className="text-zinc-700" style={{ fontSize: '0.48rem' }}>
          {docIdx + 1} / {DOCS.length}
        </span>
      </div>
    </div>
  );
};

// ─── Stats bar ────────────────────────────────────────────────────────────────

const StatItem = ({ endVal, suffix, finalOverride, label, duration, index, hasStarted, borderClasses }) => {
  const [displayValue, setDisplayValue] = useState(`0${suffix}`);

  useEffect(() => {
    if (!hasStarted) return;
    const delay = index * 120;
    let rafId;
    const timeoutId = setTimeout(() => {
      const startTime = performance.now();
      const tick = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3);
        const currentVal = Math.round(eased * endVal);
        if (progress >= 1) {
          setDisplayValue(finalOverride ?? `${endVal}${suffix}`);
        } else {
          setDisplayValue(`${currentVal}${suffix}`);
        }
        if (progress < 1) rafId = requestAnimationFrame(tick);
      };
      rafId = requestAnimationFrame(tick);
    }, delay);
    return () => { clearTimeout(timeoutId); if (rafId) cancelAnimationFrame(rafId); };
  }, [hasStarted]);

  return (
    <div className={['flex flex-col items-center py-8 px-6', borderClasses].join(' ')}>
      <span className="text-4xl sm:text-5xl font-bold text-vermillion-500 leading-none tracking-tight">
        {displayValue}
      </span>
      <span className="mt-3 text-xs font-semibold text-zinc-500 text-center uppercase" style={{ letterSpacing: '0.08em' }}>
        {label}
      </span>
    </div>
  );
};

// ─── Hero section ─────────────────────────────────────────────────────────────

const Hero = () => {
  const [hasStarted, setHasStarted] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setHasStarted(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="bg-surface-dark">
      <Container>
        <div className="pt-32 pb-16 sm:pt-40 sm:pb-20 lg:grid lg:grid-cols-2 lg:gap-12 lg:items-center">

          {/* Left: text */}
          <div className="text-center lg:text-left">
            <p className="inline-block text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-7">
              AI-Powered Process Automation
            </p>
            <h1 className="text-[3.25rem] sm:text-[4.25rem] lg:text-[4.5rem] leading-[1.05] font-bold tracking-[-0.04em] text-white">
              Accelerate<br />
              <span className="text-vermillion-600">Operational Intelligence</span>
            </h1>
            <p className="mt-7 text-[1.0625rem] leading-[1.75] text-zinc-400 max-w-[38rem] mx-auto lg:mx-0">
              Vermillion's AI platform transforms document-heavy operations, reducing processing time by 60%, eliminating errors, and empowering smarter decisions across your workflows.
            </p>
            <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center lg:justify-start">
              <Link
                to="/request-demo"
                className="inline-flex items-center justify-center py-3.5 px-8 text-[0.9375rem] font-semibold rounded-lg text-white bg-vermillion-600 border border-transparent transition-colors duration-150 hover:bg-vermillion-700"
              >
                See Vermillion in Action
              </Link>
              <a href="#solutions" className="inline-flex items-center text-[0.9375rem] font-medium text-zinc-500 transition-colors duration-150 hover:text-white">
                Explore solutions
                <svg className="ml-1.5 h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>

          {/* Right: hero visual — desktop only */}
          <div className="hidden lg:flex items-center justify-end mt-12 lg:mt-0">
            <HeroVisual />
          </div>

        </div>
      </Container>

      {/* Stats bar */}
      <div ref={statsRef} className="border-t border-white/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {stats.map((stat, i) => {
              const borderClasses = [
                i % 2 === 0 ? 'border-r border-white/10' : '',
                i < 2 ? 'border-b lg:border-b-0 border-white/10' : '',
                i > 0 && i < 3 ? 'lg:border-r lg:border-white/10' : '',
              ].join(' ');
              return (
                <StatItem
                  key={stat.label}
                  {...stat}
                  index={i}
                  hasStarted={hasStarted}
                  borderClasses={borderClasses}
                />
              );
            })}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes heroScan {
          0% { top: -32px; }
          100% { top: calc(100% + 32px); }
        }
        @keyframes heroFadeIn {
          from { opacity: 0; transform: translateX(5px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes heroFadeSlide {
          from { opacity: 0; transform: translateY(-3px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes heroLineIn {
          from { opacity: 0; transform: scaleX(0.88); transform-origin: left; }
          to { opacity: 1; transform: scaleX(1); }
        }
        @keyframes heroBlink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.25; }
        }
        @keyframes heroMarquee {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
};

export default Hero;
